import { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/hooks/useAuth';
import { useRealtimeSync } from '@/hooks/useRealtimeSync';
import { ClanSwitchProvider } from '@/contexts/ClanSwitchContext';
import { ClanSwitchLoader } from '@/components/ClanSwitchLoader';
import React from 'react';

// Create optimized QueryClient with aggressive caching to prevent reloads
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Long stale time - data stays fresh for 15 minutes
      staleTime: 15 * 60 * 1000,
      gcTime: 60 * 60 * 1000, // Keep unused data for 1 hour
      // Never refetch automatically - rely on manual invalidation and realtime sync
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      // Retry failed requests
      retry: (failureCount, error: any) => {
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Inner component that uses hooks after providers are available
function AppProviderInner({ children }: { children: ReactNode }) {
  // Set up real-time synchronization for instant updates
  // Real-time sync will invalidate queries when data changes
  useRealtimeSync();
  
  // Render app immediately without startup loader
  return (
    <>
      {children}
      <ClanSwitchLoader />
    </>
  );
}

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ClanSwitchProvider>
          <AppProviderInner>
            {children}
          </AppProviderInner>
        </ClanSwitchProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}